import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";

import { supportGetUserEvents } from "src/apiService/modules/support";
import type { SupportUserEvent } from "src/interfaces/userEvents.interface";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";
import SupportDateRangeFilter from "src/pages/support/SupportDateRangeFilter";
import {
  DayMs,
  type SupportDateRangePresetId,
} from "src/pages/support/dateRangePresets";

import EventDetailsDialog from "./EventDetailsDialog";

const useStyles = makeStyles((theme) => ({
  section: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  table: {
    marginTop: theme.spacing(1),
  },
  summaryCell: {
    maxWidth: 360,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loadMore: {
    marginTop: theme.spacing(2),
  },
}));

function eventSummary(event: SupportUserEvent) {
  const payload = event.payload || {};
  const parts = [
    event.feature,
    event.element,
    event.view,
    event.filter,
    event.text,
    event.message,
    event.source,
    event.count != null ? `count ${event.count}` : undefined,
    typeof payload.job === "string" ? payload.job : undefined,
    typeof payload.trigger === "string" ? payload.trigger : undefined,
    typeof payload.importType === "string" ? payload.importType : undefined,
    typeof payload.reason === "string" ? payload.reason : undefined,
    typeof payload.syncRunId === "string" ? payload.syncRunId : undefined,
  ].filter(Boolean);
  // Prefer a compact unique list (message often already includes job/trigger).
  return [...new Set(parts)].join(" · ") || "—";
}

interface EventsSectionProps {
  userId: string;
}

function EventsSection({ userId }: EventsSectionProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [events, setEvents] = useState<SupportUserEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [activePreset, setActivePreset] =
    useState<SupportDateRangePresetId | null>("1d");
  const [from, setFrom] = useState(() => new Date(Date.now() - DayMs));
  const [to, setTo] = useState(() => new Date());
  const [selected, setSelected] = useState<SupportUserEvent | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = useCallback(
    async (cursor?: string) => {
      if (!token || !userId) return;

      if (cursor) setLoadingMore(true);
      else setLoading(true);

      try {
        const { data } = await supportGetUserEvents(
          token,
          userId,
          from.toISOString(),
          to.toISOString(),
          { limit: 25, cursor }
        );
        setEvents((prev) => (cursor ? [...prev, ...data.events] : data.events));
        setNextCursor(data.nextCursor);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        if (!cursor) {
          setEvents([]);
          setNextCursor(null);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [dispatch, from, to, token, userId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box className={classes.section}>
      <Typography variant="h6" gutterBottom>
        Events
      </Typography>
      <SupportDateRangeFilter
        from={from}
        to={to}
        activePreset={activePreset}
        onPresetChange={(presetId, nextFrom, nextTo) => {
          setActivePreset(presetId);
          setFrom(nextFrom);
          setTo(nextTo);
        }}
        onFromChange={(date) => {
          setActivePreset(null);
          setFrom(date);
        }}
        onToChange={(date) => {
          setActivePreset(null);
          setTo(date);
        }}
      />
      {loading ? (
        <CircularProgress size={24} />
      ) : !events.length ? (
        <Typography color="textSecondary">
          No events recorded in this range.
        </Typography>
      ) : (
        <>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Version</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => {
                const summary = eventSummary(event);
                return (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      {event.date
                        ? format(new Date(event.date), "MMM d, yyyy h:mm a")
                        : "—"}
                    </TableCell>
                    <TableCell>{event.type || "—"}</TableCell>
                    <TableCell className={classes.summaryCell} title={summary}>
                      {summary}
                    </TableCell>
                    <TableCell>{event.clientVersion || "—"}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelected(event);
                          setDetailsOpen(true);
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {nextCursor ? (
            <Button
              className={classes.loadMore}
              variant="outlined"
              color="primary"
              disabled={loadingMore}
              onClick={() => void load(nextCursor)}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          ) : null}
          <EventDetailsDialog
            event={selected}
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            onExited={() => {
              setDetailsOpen((isOpen) => {
                if (!isOpen) setSelected(null);
                return isOpen;
              });
            }}
          />
        </>
      )}
    </Box>
  );
}

export default EventsSection;
